/**
 * Currency utility functions for multi-currency support
 * Supports Guatemala (GTQ), Dominican Republic (DOP), and Global (USD) markets
 */
// Country selection options for registration - ordered with priority countries first
export const getCountryOptions = (): {
  code: string;
  currency: string;
  name: string;
  flag: string;
  phonePrefix: string;
  phonePrefixes?: string[];
}[] => {
  return [
    // Priority countries first
    {
      code: 'GT', currency: 'GTQ', name: 'Guatemala', flag: '🇬🇹', phonePrefix: '+502',
    },
    {
      code: 'DO',
      currency: 'DOP',
      name: 'República Dominicana',
      flag: '🇩🇴',
      phonePrefix: '+1809',
      phonePrefixes: ['+1809', '+1829', '+1849'],
    },
    // Rest of Central America (commented out)
    // {
    //   code: 'CR', currency: 'USD', name: 'Costa Rica', flag: '🇨🇷', phonePrefix: '+506',
    // },
    // {
    //   code: 'SV', currency: 'USD', name: 'El Salvador', flag: '🇸🇻', phonePrefix: '+503',
    // },
    // {
    //   code: 'HN', currency: 'USD', name: 'Honduras', flag: '🇭🇳', phonePrefix: '+504',
    // },
    // {
    //   code: 'PA', currency: 'USD', name: 'Panamá', flag: '🇵🇦', phonePrefix: '+507',
    // },
    // South America (commented out)
    // {
    //   code: 'BO', currency: 'USD', name: 'Bolivia', flag: '🇧🇴', phonePrefix: '+591',
    // },
    // {
    //   code: 'CL', currency: 'USD', name: 'Chile', flag: '🇨🇱', phonePrefix: '+56',
    // },
    // {
    //   code: 'CO', currency: 'USD', name: 'Colombia', flag: '🇨🇴', phonePrefix: '+57',
    // },
    // {
    //   code: 'EC', currency: 'USD', name: 'Ecuador', flag: '🇪🇨', phonePrefix: '+593',
    // },
    // {
    //   code: 'PE', currency: 'USD', name: 'Perú', flag: '🇵🇪', phonePrefix: '+51',
    // },
    // {
    //   code: 'VE', currency: 'USD', name: 'Venezuela', flag: '🇻🇪', phonePrefix: '+58',
    // },
    // {
    //   code: 'BR', currency: 'USD', name: 'Brasil', flag: '🇧🇷', phonePrefix: '+55',
    // },
    // Global option
    {
      code: 'GLOBAL', currency: 'USD', name: 'Global', flag: '🌍', phonePrefix: '+502',
    },
  ];
};

export const getCurrencyByCountry = (countryCode: string): string => {
  const countryOptions = getCountryOptions();
  const country = countryOptions.find((c) => { return c.code === countryCode; });
  return country?.currency || 'USD';
};

export const getCountryNameByCurrency = (currency: string): string => {
  const currencyToCountry: Record<string, string> = {
    GTQ: 'Guatemala',
    DOP: 'República Dominicana',
    USD: 'Global',
  };
  return currencyToCountry[currency] || 'Global';
};

export const formatCurrency = (amount: number, currency: string): string => {
  const symbols: Record<string, string> = {
    GTQ: 'Q',
    DOP: 'RD$',
    USD: 'US$',
  };
  return `${symbols[currency] || 'US$'}${amount.toFixed(2)}`;
};

export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GTQ: 'Q',
    DOP: 'RD$',
    USD: 'US$',
  };
  return symbols[currency] || 'US$';
};

// Casino game currency utilities
export const parseGameCurrencies = (currencyString: string): string[] => {
  return currencyString ? currencyString.split(',').map((c) => { return c.trim(); }) : [];
};

export const canUserPlayGame = (userCurrency: string, gameCurrencies: string): boolean => {
  const supportedCurrencies = parseGameCurrencies(gameCurrencies);
  return supportedCurrencies.includes(userCurrency);
};

export const isGameCurrencyMismatch = (
  userCurrency: string,
  gameCurrency: string | null | undefined
): boolean => {
  if (!gameCurrency || gameCurrency.trim() === '') {
    return userCurrency !== 'USD';
  }

  return !canUserPlayGame(userCurrency, gameCurrency);
};

export const getAvailableCurrenciesForUser = (userCurrency: string, gameCurrencies: string): string[] => {
  const supportedCurrencies = parseGameCurrencies(gameCurrencies);
  // User can play in their currency or USD if both are supported
  const availableCurrencies = supportedCurrencies.filter((currency) => { return currency === userCurrency || currency === 'USD'; });
  return availableCurrencies;
};

export const validateCurrency = (currency: string): boolean => {
  const validCurrencies = ['GTQ', 'DOP', 'USD', 'HNL'];
  return validCurrencies.includes(currency);
};

export const getDefaultCurrency = (): string => {
  return 'USD';
};

// Get country code by 2-letter ISO code (for auto-detection)
export const getCountryCodeByISO = (isoCode: string): string => {
  const countryOptions = getCountryOptions();
  const country = countryOptions.find((c) => { return c.code === isoCode; });
  return country?.code || 'GLOBAL';
};

export const getCountryCodeFromCurrency = (currency: string): string => {
  const currencyToCountry: Record<string, string> = {
    GTQ: 'GT',
    DOP: 'DO',
    USD: 'GT', // Default to GT for USD
  };
  return currencyToCountry[currency] || 'GT';
};

export const getISOCurrencyCode = (currency: string): string => {
  const currencyToISO: Record<string, string> = {
    GTQ: 'GTQ',
    DOP: 'DOP',
    USD: 'USD',
  };
  return currencyToISO[currency] || 'USD';
};

// Get currency display name
export const getCurrencyDisplayName = (currency: string): string => {
  const displayNames: Record<string, string> = {
    GTQ: 'Quetzal Guatemalteco',
    DOP: 'Peso Dominicano',
    USD: 'Dólar Americano',
  };
  return displayNames[currency] || currency;
};

// Convert currency string to Currency enum value
export const getCurrencyEnum = (currency?: string): number => {
  const currencyMap: Record<string, number> = {
    GTQ: 1, // Currency.GTQ
    USD: 2, // Currency.USD
    DOP: 3, // Currency.DOP
  };
  return currencyMap[currency || 'USD'] || 2; // Default to USD
};

// Eukapay currencyId mapping (based on Eukapay API documentation)
export const getEukapayCurrencyId = (currency?: string): number => {
  const eukapayCurrencyMap: Record<string, number> = {
    GTQ: 4, // Eukapay currencyId for GTQ
    DOP: 5, // Eukapay currencyId for DOP
    USD: 6, // Eukapay currencyId for USD
  };
  return eukapayCurrencyMap[currency || 'USD'] || 6;
};
